import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { createLeadSchema, type CreateLeadInput, type DuplicateCandidate } from '@orion/contracts';
import { Button, Input, Select } from '@orion/ui';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useToast } from '../../lib/toast.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { LeadsListPage } from './LeadsListPage.js';
import { useCreateLead, useDuplicateCheck, useLeadSourceOptionsQuery, useLeadStatusOptionsQuery } from './api.js';

/** docs/architecture screen "New lead + duplicate check"
 * (docs/architecture/10-lead-deduplication.md is folded into
 * 08-v1-scope.md — "Use existing / Create anyway / Merge"). Wires the real
 * duplicate-check endpoint before ever calling create, and real status/
 * source/owner pickers (design/orion-crm-design/rendered-html/NewLead.html's
 * own dropdowns).
 *
 * design/orion-crm-design/rendered-html/NewLead.html renders this as a
 * 520px slide-over drawer above the real Leads list, not a standalone
 * page — so `/leads/new` renders the real LeadsListPage behind a backdrop
 * and this drawer on top, rather than navigating away from it. Not built:
 * the design's "assign automatically (round robin)" default (no
 * round-robin assignment feature exists), live duplicate-matching while
 * typing (only checked on submit), and an existing-company name-match hint
 * (companyName is free text pre-conversion, there's no Company
 * search-as-you-type here yet). */
export function NewLeadPage() {
  return (
    <>
      <LeadsListPage />
      <div className="fixed inset-0 z-40 bg-night/42" />
      <aside role="dialog" aria-modal="true" aria-labelledby="new-lead-title" className="fixed inset-y-0 right-0 z-50 flex w-130 flex-col bg-surface shadow-dialog">
        <NewLeadForm />
      </aside>
    </>
  );
}

function NewLeadForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[] | null>(null);
  const duplicateCheck = useDuplicateCheck();
  const createLead = useCreateLead();
  const { data: statuses } = useLeadStatusOptionsQuery();
  const { data: sources } = useLeadSourceOptionsQuery();
  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<CreateLeadInput>({
    resolver: zodResolver(createLeadSchema),
    defaultValues: { priority: 'normal', tagIds: [], customFields: {}, confirmDuplicate: false },
  });

  const submitLead = async (input: CreateLeadInput) => {
    const result = await createLead.mutateAsync(input);
    if (result.status === 'created') {
      await navigate({ to: '/leads/$leadId', params: { leadId: result.lead.id } });
      return;
    }
    if (result.status === 'needs_review') {
      toast.success("AI wasn't confident this is a genuine lead — it's queued in AI Lead Review for a decision, not created yet.");
    } else if (result.status === 'rejected') {
      toast.error("AI qualification determined this isn't a genuine sales lead, so it wasn't created. You can still check AI Lead Review's history.");
    } else {
      toast.error('AI qualification failed — nothing was created. It was preserved for retry in AI Lead Review.');
    }
    await navigate({ to: '/ai-review' });
  };

  const onSubmit = async (input: CreateLeadInput) => {
    const found = await duplicateCheck.mutateAsync(input);
    if (found.length > 0) {
      setDuplicates(found);
      return;
    }
    await submitLead(input);
  };

  const createAnyway = async () => {
    await submitLead({ ...getValues(), confirmDuplicate: true });
  };

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-6">
        <div className="flex flex-1 flex-col gap-0.5">
          <h1 id="new-lead-title" className="text-[17px] font-[650] leading-5.5 text-ink">
            New lead
          </h1>
          <span className="text-xs text-text-muted">
            Fields marked <span className="text-danger">*</span> are required
          </span>
        </div>
        <button type="button" aria-label="Close" onClick={() => navigate({ to: '/leads' })} className="flex size-8 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </header>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
          {duplicates && duplicates.length > 0 && (
            <div role="alert" className="mb-1.5 flex gap-3 rounded-xl bg-amber-soft-2 p-3.5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A4B06" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
                <path d="M12 3 2 20h20z" />
                <path d="M12 10v4M12 17h.01" />
              </svg>
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="text-sm font-semibold text-amber-text">Potential duplicate found</span>
                {duplicates.map((d) => (
                  <span key={d.leadId} className="text-[13px] leading-4.5 text-amber-text">
                    Existing lead: <strong className="font-semibold">{d.fullName}</strong> · {d.email ?? 'no email'} · {d.companyName ?? 'no company'} · {d.statusName}
                  </span>
                ))}
                <div className="mt-2 flex gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={() => navigate({ to: '/leads/$leadId', params: { leadId: duplicates[0]!.leadId } })}>
                    Use existing
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => setDuplicates(null)}>
                    Edit lead
                  </Button>
                  <Button type="button" size="sm" onClick={createAnyway} loading={isSubmitting}>
                    Create anyway
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Input label="First name" error={errors.firstName?.message} {...register('firstName')} />
            <Input label="Last name" error={errors.lastName?.message} {...register('lastName')} />
          </div>
          <Input label="Email" type="email" error={errors.email?.message} {...register('email')} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Phone" error={errors.phone?.message} {...register('phone')} />
            <Input label="Job title" error={errors.jobTitle?.message} {...register('jobTitle')} />
          </div>
          <Input label="Company" error={errors.companyName?.message} {...register('companyName')} />

          <div className="grid grid-cols-2 gap-3">
            <Select label="Lead source" placeholder="Select a source" options={(sources ?? []).map((s) => ({ value: s.id, label: s.name }))} {...register('sourceId')} />
            <Select label="Status" placeholder="Default status" options={(statuses ?? []).map((s) => ({ value: s.id, label: s.name }))} {...register('statusId')} />
          </div>

          <Select label="Owner" placeholder="Unassigned" options={(members ?? []).map((m) => ({ value: m.id, label: memberLabels.get(m.id) ?? m.displayName }))} {...register('ownerId')} />

          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-[13px] font-semibold text-ink">Priority</legend>
            <div role="radiogroup" className="grid h-9 grid-cols-3 gap-0.5 rounded-control border border-border-strong bg-surface p-0.75">
              {(['low', 'normal', 'high'] as const).map((p) => (
                <label key={p} className="flex cursor-pointer items-center justify-center gap-1 rounded-[6px] text-[13px] font-semibold text-text-secondary has-checked:bg-primary-soft has-checked:text-primary-text">
                  <input type="radio" value={p} className="sr-only" {...register('priority')} />
                  {p === 'low' ? 'Low' : p === 'normal' ? 'Medium' : 'High'}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <footer className="flex h-16 shrink-0 items-center gap-2 border-t border-border px-6">
          <span className="flex flex-1 items-center gap-1 text-xs text-text-muted">
            <kbd className="rounded-xs border border-border bg-ground px-1 font-mono text-[11px] text-text-secondary">Ctrl</kbd>
            <kbd className="rounded-xs border border-border bg-ground px-1 font-mono text-[11px] text-text-secondary">↵</kbd>
            to save
          </span>
          <Button type="button" variant="secondary" onClick={() => navigate({ to: '/leads' })}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting || duplicateCheck.isPending}>
            Save lead
          </Button>
        </footer>
      </form>
    </>
  );
}
